import { EmailStateItem } from 'store/email/email.type';
import BaseNode, { BaseNodeProps, FlowNodeMenuItem } from 'components/FlowBuilder/FlowNodes/BaseNode/BaseNode';
import React, { useCallback, useMemo } from 'react';
import IconEmail from 'pages/FlowBuilder/assets/icons/IconEmail';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import OptionalNodeWrapper from '../OptionalNodeWrapper/OptionalNodeWrapper';
import useJourneyStatus from 'hooks/useJourneyStatus';
import { isEmpty } from 'lodash';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import { validateStepAction } from 'validators/ExperienceCanvas/journey.validator';
import { FlowNodeTypes } from '../node.types';
import { TOUCHPOINT_STATUSES } from 'components/ExperienceCanvas/types';
import { ERROR_TYPES } from 'pages/ExperienceCanvas/types';
import { getTooltipIcon } from 'pages/FlowBuilder/helpers/flow-builder.helpers';

import './EmailNode.scss';
import { FlightTooltip } from '@flybits/design-system';
import { JourneyComponentTypes } from 'pages/FlowBuilder/types/flow-builder.types';

const MAIN_CLASS = 'email-node';
const CLASSES = {
  CONFIGURED: `${MAIN_CLASS}--configured`,
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_TOOLTIP: `${MAIN_CLASS}__label__tooltip`,
  LABEL_DISABLED: `${MAIN_CLASS}__label--disabled`,
  LABEL_TEXT: `${MAIN_CLASS}__label__text`,
  LABEL_NAME: `${MAIN_CLASS}__label__name`,
  LABEL_BUTTON: `${MAIN_CLASS}__label__button`,
};

type EmailNodeProps = {
  data: {
    email: EmailStateItem;
    stepIndex: number;
  };
};

function EmailNode({ data: { email, stepIndex } }: EmailNodeProps) {
  const { isJourneyLocked } = useJourneyStatus();
  const dependencyGraph = useSelector((state) => state.te.journey.dependencyGraph);
  const {
    sidePanels: { email: emailSidePanel },
    modals: {
      rename: { setData: setRenameModalData },
    },
  } = useFlowBuilderContext();

  const { errorStatus, errorMessage } = useSelector((state) => {
    const { errorStatus, errors } = validateStepAction(state, stepIndex, FlowNodeTypes.EMAIL, email.refId);
    // currently only revealing the first error on a node.
    const errorMessage = errors[0]?.err ?? '';

    if (errorStatus === '') {
      return { errorStatus: TOUCHPOINT_STATUSES.COMPLETE, errorMessage };
    } else if (errorStatus === ERROR_TYPES.INCOMPLETE) {
      return { errorStatus: TOUCHPOINT_STATUSES.INCOMPLETE, errorMessage };
    } else {
      return { errorStatus: TOUCHPOINT_STATUSES.ERROR, errorMessage };
    }
  });

  const isOptional = email.isOptional;
  const isOptionalEmailEnabled = !!email.enableOptionalNode;

  const isConfigured = errorStatus !== TOUCHPOINT_STATUSES.INCOMPLETE;

  const handleSetupEmail = useCallback(() => {
    emailSidePanel.setStepIndex(stepIndex);
    emailSidePanel.setEmailContext({
      isOpen: true,
      isViewOnly: isJourneyLocked,
      email,
    });
  }, [email, emailSidePanel, stepIndex, isJourneyLocked]);

  const handleRenameEmail = useCallback(() => {
    setRenameModalData({
      entity: email,
      entityType: JourneyComponentTypes.EMAIL,
      entityNodeName: email.name,
      stepIndex,
    });
  }, [email, setRenameModalData, stepIndex]);

  const baseNodeProps = useMemo<Omit<BaseNodeProps, 'children'>>(() => {
    const menuItems: FlowNodeMenuItem[] = [];
    if (isConfigured) {
      if (isJourneyLocked) {
        menuItems.push({
          icon: 'eyeOpenOutline',
          label: 'View Email',
          onClick: handleSetupEmail,
          iconRight: getTooltipIcon(errorStatus),
          iconRightTooltip: errorMessage,
        });
      } else {
        menuItems.push(
          {
            icon: 'editOutline',
            label: 'Edit Email',
            onClick: handleSetupEmail,
            iconRight: getTooltipIcon(errorStatus),
            iconRightTooltip: errorMessage,
          },
          {
            icon: 'textBoxWithCorners',
            label: 'Rename',
            onClick: handleRenameEmail,
          },
        );
      }
    }

    return {
      className: `${MAIN_CLASS} ${isConfigured ? '' : `${MAIN_CLASS}--unconfigured`}`,
      Icon: IconEmail,
      menuItems,
    };
  }, [errorMessage, errorStatus, handleSetupEmail, isConfigured, handleRenameEmail, isJourneyLocked]);

  const renderEmailNode = () => (
    <div
      className={`${CLASSES.LABEL} ${
        shouldShowOptionalNodeWrapper && !isOptionalEmailEnabled ? CLASSES.LABEL_DISABLED : ''
      }`}
    >
      <div className={CLASSES.LABEL_TEXT}>Send an Email</div>
      {isConfigured ? (
        <FlightTooltip description={email.name || 'Email Template'} className={CLASSES.LABEL_TOOLTIP}>
          <div className={CLASSES.LABEL_NAME}>{email.name || 'Email Template'}</div>
        </FlightTooltip>
      ) : (
        <button className={CLASSES.LABEL_BUTTON} onClick={handleSetupEmail}>
          <span>+</span>
          Set up Email
        </button>
      )}
    </div>
  );

  const shouldShowOptionalNodeWrapper = useMemo(() => {
    if (isJourneyLocked) {
      return false;
    }
    let showWrapper = false;
    if (isEmpty(dependencyGraph?.[email.refId]?.isRefBy) && isOptional) {
      showWrapper = true;
    }
    return showWrapper;
  }, [isJourneyLocked, dependencyGraph, email.refId, isOptional]);

  return shouldShowOptionalNodeWrapper ? (
    <OptionalNodeWrapper isEnabled={isOptionalEmailEnabled} onToggle={() => null}>
      <BaseNode {...baseNodeProps} isOptional isDisabled={!isOptionalEmailEnabled}>
        {renderEmailNode()}
      </BaseNode>
    </OptionalNodeWrapper>
  ) : (
    <BaseNode {...baseNodeProps}>{renderEmailNode()}</BaseNode>
  );
}

export default EmailNode;
